import React, {useCallback, useEffect, useRef} from 'react';
import {
    Modal, Box, Typography, Stack, IconButton,
    Divider, useTheme, alpha,
} from '@mui/material';
import MyChip from '../../../styles/theme/customizations/MyChip';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import dayjs from 'dayjs';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';

import {IAlert} from '../../../models/IAlert';
import {selectAlertList, selectAlertModalOpen, selectUnreadCount} from "../model/selectors";
import {closeAlertModal} from "../model/slice";
import {ALERT_LABELS, SEVERITY_COLOR} from "../utils/alertUtils";
import {fetchMarkAlertsRead} from "../model/actions";

export const AlertModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isOpen = useAppSelector(selectAlertModalOpen);
    const alerts = useAppSelector(selectAlertList);
    const unread = useAppSelector(selectUnreadCount);
    const markTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Через 1 сек после открытия — помечаем все непрочитанные как прочитанные
    useEffect(() => {
        if (!isOpen) return;
        const unreadIds = alerts.filter(a => !a.is_read).map(a => a.id);
        if (!unreadIds.length) return;

        markTimerRef.current = setTimeout(() => {
            void dispatch(fetchMarkAlertsRead(unreadIds));
        }, 1000);

        return () => {
            if (markTimerRef.current) clearTimeout(markTimerRef.current);
        };
    }, [alerts, isOpen, dispatch]);

    const handleClose = useCallback(() => {
        dispatch(closeAlertModal());
    }, [dispatch]);

    const isDark = theme.palette.mode === 'dark';

    const severityBg = (severity: IAlert['severity']) => {
        const map = {
            info: alpha(theme.palette.success.main, isDark ? 0.15 : 0.08),
            warning: alpha(theme.palette.warning.main, isDark ? 0.15 : 0.08),
            critical: alpha(theme.palette.error.main, isDark ? 0.15 : 0.08),
        };
        return map[severity];
    };

    return (
        <Modal open={isOpen} onClose={handleClose} keepMounted={false}>
            <Box sx={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: {xs: '95vw', sm: 560},
                maxHeight: '80vh',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: 24,
                overflow: 'hidden',
                outline: 'none',
            }}>

                {/* Шапка */}
                <Box sx={{
                    px: 3, py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: isDark
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.04),
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <NotificationsIcon color="primary"/>
                        <Typography variant="h6" fontWeight={600}>
                            Уведомления
                        </Typography>
                        {unread.count > 0 && (
                            <MyChip
                                label={`${unread.count} новых`}
                                status={unread.has_critical ? 'error' : 'warning'}
                                sx={{fontWeight: 600}}
                            />
                        )}
                    </Stack>
                    <IconButton size="small" onClick={handleClose}>
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                </Box>

                {/* Список */}
                <Box sx={{overflowY: 'auto', flex: 1}}>
                    {alerts.length === 0 ? (
                        <Stack alignItems="center" justifyContent="center" sx={{py: 8}} spacing={1}>
                            <NotificationsIcon sx={{fontSize: 48, color: 'text.disabled'}}/>
                            <Typography color="text.secondary">Уведомлений нет</Typography>
                        </Stack>
                    ) : (
                        alerts.map((alert, idx) => (
                            <React.Fragment key={alert.id}>
                                <Box sx={{
                                    px: 3, py: 1.5,
                                    bgcolor: alert.is_read ? 'transparent' : severityBg(alert.severity),
                                    transition: 'background-color 0.4s ease',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.action.hover, 0.06),
                                    },
                                }}>
                                    <Stack direction="row" alignItems="flex-start" spacing={1.5}>

                                        {/* Цветная полоска слева */}
                                        <Box sx={{
                                            width: 3,
                                            minHeight: 40,
                                            borderRadius: 4,
                                            bgcolor: `${SEVERITY_COLOR[alert.severity]}.main`,
                                            flexShrink: 0,
                                            mt: 0.3,
                                        }}/>

                                        <Box sx={{flex: 1, minWidth: 0}}>
                                            <Stack direction="row" alignItems="center"
                                                   justifyContent="space-between" spacing={1}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={alert.is_read ? 400 : 600}
                                                    noWrap
                                                >
                                                    {ALERT_LABELS[alert.type]}
                                                </Typography>
                                                <Stack direction="row" alignItems="center" spacing={0.5}
                                                       sx={{flexShrink: 0}}>
                                                    {!alert.is_read && (
                                                        <Box sx={{
                                                            width: 7, height: 7,
                                                            borderRadius: '50%',
                                                            bgcolor: `${SEVERITY_COLOR[alert.severity]}.main`,
                                                        }}/>
                                                    )}
                                                    <Typography variant="caption" color="text.disabled" noWrap>
                                                        {dayjs(alert.triggered_at).format('DD.MM HH:mm')}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" alignItems="center"
                                                   justifyContent="space-between" spacing={1}>
                                                {/* Машина */}
                                                {alert.machinery_title && (
                                                    <Typography variant="caption"
                                                                color="text.disabled"
                                                                sx={{display: 'block', mt: 0.2}}>
                                                        {alert.machinery_title}
                                                    </Typography>
                                                )}
                                                {/* Payload */}
                                                {alert.payload && (
                                                    <Typography variant="caption" color="text.secondary"
                                                                sx={{display: 'block', mt: 0.3}}>
                                                        {formatPayload(alert)}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Box>
                                {idx < alerts.length - 1 && (
                                    <Divider sx={{mx: 3, opacity: 0.5}}/>
                                )}
                            </React.Fragment>
                        ))
                    )}
                </Box>

                {/* Футер */}
                <Box sx={{
                    px: 3, py: 1.5,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.default',
                }}>
                    <Typography variant="caption" color="text.disabled">
                        Показаны события за последние 7 дней
                    </Typography>
                </Box>
            </Box>
        </Modal>
    );
};

// Человекочитаемый payload
function formatPayload(alert: IAlert): string {
    const p = alert.payload as Record<string, unknown>;
    switch (alert.type) {
        case 'LOW_FUEL':
            return `${(p.liters as number)?.toFixed(1)} л`;
        case 'LOW_VOLTAGE':
        case 'HIGH_VOLTAGE':
            return `${(p.voltage as number)?.toFixed(2)} В`;
        case 'OIL_LOW':
        case 'OIL_HIGH':
            return `${p.pressure as number} кПа`;
        case 'HIGH_RPM':
            return `${p.rpm as number} об/мин`;
        case 'BEACON_LOW_BATTERY':
            return `${p.percent as number} %`;
        case 'ENGINE_OVERHEAT':
            return `${p.temp as number} °C`;
        case 'OVERSPEED':
            return `${p.speed as number} км/ч (лимит ${p.limit as number})`;
        case 'FUEL_REFILL':
            return `+${(p.delta as number)?.toFixed(1)} л`;
        case 'FUEL_DRAIN':
            return `-${(p.delta as number)?.toFixed(1)} л`;
        default:
            return '';
    }
}
